import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Trash2, X, FileText, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { useEmployeeApi } from "../employeeApi";

const GROUPS = [
    { key: "before", label: "Før", accept: "image/*" },
    { key: "after", label: "Efter", accept: "image/*" },
    { key: "receipt", label: "Kvitteringer", accept: "image/*,application/pdf" },
];

// Komprimér billeder klient-side (maks 1600 px, JPEG) så mobil-upload holder sig under proxy-grænser.
async function compressImage(file) {
    if (!file.type.startsWith("image/") || file.type === "image/heic" || file.type === "image/heif") return file;
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
        const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
        if (scale === 1 && file.size < 900 * 1024) return file;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale); canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
        return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
    } catch { return file; } finally { URL.revokeObjectURL(url); }
}

function Thumb({ jobId, file, onOpen, onDelete }) {
    const api = useEmployeeApi();
    const [src, setSrc] = useState("");
    useEffect(() => {
        let url = "";
        if (file.content_type === "application/pdf") return undefined;
        api.getFileBlob(jobId, file.id).then((b) => { url = URL.createObjectURL(b); setSrc(url); }).catch(() => {});
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [api, jobId, file.id, file.content_type]);
    return (
        <div className="relative w-[88px] h-[88px] rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shrink-0" data-testid={`job-file-${file.kind}`}>
            <button type="button" onClick={() => src && onOpen(src)} className="w-full h-full flex items-center justify-center">
                {file.content_type === "application/pdf" ? <FileText className="w-7 h-7 text-slate-400" /> : src ? <img src={src} alt={file.caption || file.kind} className="w-full h-full object-cover" /> : <Loader2 className="w-4 h-4 animate-spin text-slate-300" />}
            </button>
            <button type="button" onClick={() => onDelete(file)} data-testid="job-file-delete" className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-red-600 flex items-center justify-center shadow"><Trash2 className="w-3 h-3" /></button>
        </div>
    );
}

export default function JobFiles({ jobId }) {
    const api = useEmployeeApi();
    const [files, setFiles] = useState(null);
    const [uploading, setUploading] = useState("");
    const [preview, setPreview] = useState("");
    const inputs = useRef({});

    const load = () => api.getFiles(jobId).then((d) => setFiles(d.files || [])).catch(() => setFiles([]));
    useEffect(() => { load(); }, [api, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

    const pick = async (kind, e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setUploading(kind);
        try {
            const prepared = kind === "receipt" && f.type === "application/pdf" ? f : await compressImage(f);
            if (prepared.size > 10 * 1024 * 1024) { toast.error("Filen er for stor (maks 10 MB)"); return; }
            await api.uploadFile(jobId, prepared, kind);
            await load();
            toast.success("Gemt i filarkivet");
        } catch (err) { toast.error(err?.response?.data?.detail || "Upload mislykkedes"); }
        finally { setUploading(""); }
    };
    const del = async (f) => {
        try { await api.deleteFile(jobId, f.id); setFiles((l) => l.filter((x) => x.id !== f.id)); toast.success("Fil slettet"); }
        catch { toast.error("Kunne ikke slette"); }
    };

    return (
        <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="job-files">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" />Dokumentation</h2>
            {files === null ? <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div> : (
                <div className="space-y-3">
                    {GROUPS.map((g) => {
                        const items = files.filter((f) => f.kind === g.key);
                        return (
                            <div key={g.key} data-testid={`files-group-${g.key}`}>
                                <p className="text-xs font-semibold text-slate-600 mb-1.5">{g.label} <span className="text-slate-400">({items.length})</span></p>
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                    <input ref={(el) => (inputs.current[g.key] = el)} type="file" accept={g.accept} capture={g.key === "receipt" ? undefined : "environment"} className="hidden" onChange={(e) => pick(g.key, e)} data-testid={`file-input-${g.key}`} />
                                    <button type="button" onClick={() => inputs.current[g.key]?.click()} disabled={!!uploading} data-testid={`file-add-${g.key}`}
                                        className="w-[88px] h-[88px] rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/60 text-blue-700 flex flex-col items-center justify-center gap-1 text-[11px] font-semibold shrink-0 active:scale-95 transition-transform disabled:opacity-50">
                                        {uploading === g.key ? <Loader2 className="w-5 h-5 animate-spin" /> : g.key === "receipt" ? <ImagePlus className="w-5 h-5" /> : <Camera className="w-5 h-5" />}
                                        {uploading === g.key ? "Uploader…" : g.key === "receipt" ? "Tilføj" : "Tag billede"}
                                    </button>
                                    {items.map((f) => <Thumb key={f.id} jobId={jobId} file={f} onOpen={setPreview} onDelete={del} />)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <p className="text-[11px] text-slate-400 mt-2">Gemmes privat i filarkivet — kun opgavens medarbejder og ejer har adgang.</p>
            {preview && (
                <div className="absolute inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setPreview("")} data-testid="file-preview">
                    <img src={preview} alt="Forhåndsvisning" className="max-w-full max-h-full object-contain" />
                    <button className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/20 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
                </div>
            )}
        </section>
    );
}
